import React, { useId } from 'react';
import { KRAJ } from '../../kraj';

/**
 * Rysunek kafelka „Oferta pod lupą”: kartka oferty na fotowoltaikę, po której
 * chodzi lupa i przygląda się kolejnym pozycjom.
 *
 * ══ CO NA NIM JEST I DLACZEGO ══
 * Każdy element odpowiada czemuś, co raport naprawdę robi, więc rysunek nie
 * obiecuje niczego ponad moduł:
 *   - pozycje z zieloną fajką albo czerwoną flagą = czerwone flagi w raporcie,
 *   - pasek z zielonym odcinkiem i znacznikiem = „typowy zakres” ceny,
 *   - okrągła pieczątka w rogu = oferta przejrzana.
 *
 * Drobne znaki PV (prośba właściciela 2026-09-30, żeby było widać, że to oferta
 * na fotowoltaikę, a nie dowolny dokument): słońce w nagłówku, przy pozycjach
 * moduł, falownik i magazyn, a w rogu metka „zł”. Złotówki, a nie znak dolara:
 * oferty i ceny w serwisie są w złotych (poza Polską waluta kraju, KRAJ.symbol). Czerwona flaga stoi przy magazynie,
 * bo niezgodny magazyn to najczęstsza twarda flaga z raportu.
 *
 * ══ LUPA ══
 * Zapętlona na prośbę właściciela: zatrzymuje się kolejno na module, falowniku,
 * czerwonej fladze przy magazynie, zakresie ceny i metce „zł”, czyli na tym,
 * co raport faktycznie sprawdza. W szkle widać POWIĘKSZONY fragment kartki:
 * druga kopia kartki, przeskalowana i przycięta do okręgu soczewki. Obie części
 * lupy (ramka i obraz w szkle) poruszają się dwiema animacjami z tymi samymi
 * punktami, więc powiększenie zawsze pokazuje to, co leży pod szkłem.
 * Pozycje i czasy są w `index.css` (`vvLupaRuch`, `vvLupaObraz`) i muszą
 * zgadzać się parami, inaczej w szkle pojawi się inny fragment niż pod nim.
 *
 * Pozycje są paskami, a nie tekstem: przy ok. 250 px szerokości litery byłyby
 * nieczytelne, a zmyślone nazwy i kwoty czytałyby się jak prawdziwa oferta.
 *
 * Kolory wpisane na sztywno, a nie klasami Tailwind: kartka ma zostać biała
 * także w ciemnym motywie.
 *
 * Rysunek jest dekoracyjny (`aria-hidden`), sens niesie tytuł kafelka.
 */

const NIEB = '#2563eb';
const NIEB_TLO = '#dbeafe';
const GRANAT = '#1e3a8a';
const ZIELONY = '#059669';
const ZIELONY_TLO = '#d1fae5';
const CZERWONY = '#dc2626';
const CZERWONY_TLO = '#fee2e2';
const SLONCE = '#f59e0b';
const PASEK = '#e2e8f0';
const PASEK_CIEMNY = '#cbd5e1';

// Promień soczewki w jednostkach rysunku (szerokość rysunku: 240).
const PROMIEN_LUPY = 16;

// Fajka w kółku, rysowana względem środka (cx, cy).
const Fajka = ({ cx, cy, opoznienie }) => (
  <g className="vv-oferta-wpis" style={{ animationDelay: opoznienie }}>
    <circle cx={cx} cy={cy} r="7" fill={ZIELONY_TLO} />
    <path d={`M${cx - 3.2} ${cy + 0.2} l2.2 2.2 4.4 -4.8`} fill="none" stroke={ZIELONY}
          strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
  </g>
);

// Ikonki sprzętu przy pozycjach: kontur w firmowym niebieskim, jasne wypełnienie.
const Modul = () => (
  <g>
    <rect x="52.5" y="59" width="15" height="10" rx="1.5" fill={NIEB_TLO} stroke={NIEB} strokeWidth="1.3" />
    <path d="M57.5 59v10M62.5 59v10M52.5 64h15" stroke={NIEB} strokeWidth="0.9" />
  </g>
);

const Falownik = () => (
  <g>
    <rect x="54" y="73" width="12" height="14" rx="2.5" fill={NIEB_TLO} stroke={NIEB} strokeWidth="1.3" />
    {/* Sinusoida: falownik zamienia prąd stały z modułów na przemienny. */}
    <path d="M56.6 80.5c0.9 -2.2 2.1 -2.2 3 0s2.1 2.2 3 0" fill="none" stroke={NIEB}
          strokeWidth="1.2" strokeLinecap="round" />
    <circle cx="63.4" cy="76" r="0.9" fill="#10b981" />
  </g>
);

const Magazyn = () => (
  <g>
    <rect x="52.5" y="91.5" width="13" height="9" rx="2" fill={NIEB_TLO} stroke={NIEB} strokeWidth="1.3" />
    <rect x="65.9" y="94.2" width="1.9" height="3.6" rx="0.8" fill={NIEB} />
    <rect x="54.5" y="93.5" width="5.5" height="5" rx="1" fill={NIEB} />
  </g>
);

// Cała kartka razem z metką i pieczątką. Rysowana DWA razy: raz normalnie,
// raz powiększona w szkle lupy.
const Kartka = () => (
  <g>
    <rect x="18" y="6" width="196" height="136" rx="14" fill="#ffffff" />

    {/* Nagłówek: słońce (oferta na fotowoltaikę) i dwie linie tytułu */}
    <rect x="32" y="20" width="24" height="24" rx="7" fill="#fef3c7" />
    <circle cx="44" cy="32" r="4" fill={SLONCE} />
    <path d="M44 23.5v2M44 38.5v2M35.5 32h2M50.5 32h2M38 26l1.4 1.4M48.6 36.6l1.4 1.4M38 38l1.4 -1.4M48.6 27.4l1.4 -1.4"
          stroke={SLONCE} strokeWidth="1.6" strokeLinecap="round" />
    <rect x="64" y="23" width="72" height="7" rx="3.5" fill={GRANAT} opacity="0.9" />
    <rect x="64" y="35" width="46" height="5" rx="2.5" fill={PASEK_CIEMNY} />
    <line x1="32" y1="52" x2="200" y2="52" stroke="#eef2f7" strokeWidth="1.5" />

    {/* Pozycje oferty: status, sprzęt, nazwa i kwota */}
    <Fajka cx={38} cy={64} opoznienie="0.25s" />
    <Modul />
    <rect x="74" y="61" width="78" height="6" rx="3" fill={PASEK} />
    <rect x="172" y="61" width="28" height="6" rx="3" fill={PASEK_CIEMNY} />

    <Fajka cx={38} cy={80} opoznienie="0.45s" />
    <Falownik />
    <rect x="74" y="77" width="60" height="6" rx="3" fill={PASEK} />
    <rect x="172" y="77" width="28" height="6" rx="3" fill={PASEK_CIEMNY} />

    {/* Czerwona flaga przy magazynie: pozycja do wyjaśnienia z instalatorem */}
    <g className="vv-oferta-wpis" style={{ animationDelay: '0.65s' }}>
      <circle cx="38" cy="96" r="7" fill={CZERWONY_TLO} />
      <path d="M35.6 91.6v9" stroke={CZERWONY} strokeWidth="1.6" strokeLinecap="round" />
      <path d="M35.6 92.1h6.4l-1.8 2.4 1.8 2.4h-6.4z" fill={CZERWONY} />
    </g>
    <Magazyn />
    <rect x="74" y="93" width="86" height="6" rx="3" fill={PASEK} />
    <rect x="172" y="93" width="28" height="6" rx="3" fill={PASEK_CIEMNY} />

    {/* Typowy zakres ceny: szary tor, zielony odcinek i znacznik oferty */}
    <rect x="32" y="110" width="40" height="4" rx="2" fill={PASEK_CIEMNY} />
    <rect x="32" y="120" width="150" height="8" rx="4" fill={PASEK} />
    <rect x="78" y="120" width="62" height="8" rx="4" fill="#6ee7b7" />
    <circle className="vv-oferta-znacznik" cx="118" cy="124" r="6"
            fill={NIEB} stroke="#ffffff" strokeWidth="2.5" />

    {/* Metka z ceną: wystaje poza prawą krawędź kartki jak naklejka.
        Obrót siedzi w WEWNĘTRZNEJ grupie: animacja wejścia ustawia własne
        `transform` i nadpisałaby obrót, gdyby oba były na jednym elemencie. */}
    <g className="vv-oferta-wpis" style={{ animationDelay: '1.1s' }}>
      <g transform="rotate(-8 196 29)">
        <path d="M178 19h39a5 5 0 0 1 5 5v10a5 5 0 0 1 -5 5h-39l-8 -10z" fill="#fbbf24" />
        <circle cx="177.5" cy="29" r="2" fill="#ffffff" />
        <text x="200" y="33" textAnchor="middle" fontSize="11" fontWeight="800" fill="#78350f">{KRAJ.symbol}</text>
      </g>
    </g>

    {/* Pieczątka „przejrzane”: wystaje poza róg kartki jak naklejka */}
    <g className="vv-oferta-wpis" style={{ animationDelay: '0.95s' }}>
      <circle cx="206" cy="126" r="17" fill="#10b981" stroke="#ffffff" strokeWidth="4" />
      <path d="M198.5 126.5l5 5 9.5-10" fill="none" stroke="#ffffff"
            strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" />
    </g>
  </g>
);

/**
 * Lupa rysowana wokół własnego środka (0, 0); na miejsce przesuwa ją klasa
 * `vv-lupa`. Obraz w szkle to druga kartka w klasie `vv-lupa-obraz`, czyli
 * `scale(1.5) translate(-x, -y)`: punkt kartki leżący pod środkiem szkła trafia
 * w środek soczewki, a wszystko wokół niego jest półtora raza większe.
 */
const Lupa = ({ idSoczewki }) => (
  <g className="vv-lupa">
    {/* Cień na kartce: spod szkła wystaje tylko sierp w prawym dolnym rogu,
        dzięki czemu lupa wygląda, jakby wisiała nad kartką. */}
    <circle cx="3" cy="4" r={PROMIEN_LUPY + 1} fill="#0f172a" opacity="0.14" />

    <g clipPath={`url(#${idSoczewki})`}>
      <rect x={-PROMIEN_LUPY} y={-PROMIEN_LUPY} width={PROMIEN_LUPY * 2} height={PROMIEN_LUPY * 2} fill="#ffffff" />
      <g className="vv-lupa-obraz">
        <Kartka />
      </g>
    </g>

    {/* Szkło: lekki niebieski odcień i odblask w lewym górnym rogu */}
    <circle r={PROMIEN_LUPY} fill="#bfdbfe" opacity="0.14" />
    <path d="M-10.5 -5.5a11.5 11.5 0 0 1 5.5 -5.5" fill="none" stroke="#ffffff"
          strokeWidth="2.2" strokeLinecap="round" opacity="0.85" />
    <circle r={PROMIEN_LUPY} fill="none" stroke={GRANAT} strokeWidth="3.5" />
    <path d="M12 12L24 24" stroke={GRANAT} strokeWidth="6.5" strokeLinecap="round" />
  </g>
);

const GrafikaOferta = ({ className = '' }) => {
  // Identyfikator przycięcia musi być unikalny na stronie; `useId` zwraca go
  // z dwukropkami, które w odwołaniu `url(#…)` lepiej pominąć.
  const idSoczewki = `vv-soczewka-${useId().replace(/:/g, '')}`;
  return (
    <div className={className} aria-hidden="true">
      <svg viewBox="0 0 240 150" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto">
        <defs>
          <clipPath id={idSoczewki}>
            <circle r={PROMIEN_LUPY} />
          </clipPath>
        </defs>
        <Kartka />
        <Lupa idSoczewki={idSoczewki} />
      </svg>
    </div>
  );
};

export default GrafikaOferta;
